// Purpose: Maps BayHive primitives to Lara's semantic color meanings without redesigning components.
import {
  bayhiveBase,
  bayhiveDark,
  bayhiveMask,
  bayhivePurple,
  bayhiveViolet,
  createSemanticRgbScale,
  createSemanticScale,
  createSemanticState,
  createSeverityState,
} from './shared.js';
import { bayhiveWorkflowDark, bayhiveWorkflowLight } from './workflow.js';

// Preserve the light-mode treatment; dark mode supplies a solid indicator.
const bayhiveFieldFocusRing = {
  width: '0',
  style: 'none',
  color: 'transparent',
  offset: '0',
  shadow: '0 0 0 0.2rem color-mix(in srgb, {primary.color}, transparent 72%)',
};

// Separate the light ring from bright selected controls with a dark inner gap.
const darkFocusShadow = '0 0 0 2px {surface.950}, 0 0 0 4px {focus.ring.color}';

export const bayhiveSemantic = {
  success: createSemanticScale('bayhive.success'),
  danger: createSemanticScale('bayhive.danger'),
  warn: createSemanticScale('bayhive.warn'),
  // Instant option feedback avoids a trail of highlights while browsing lists.
  list: { option: { transitionDuration: '0s' } },
  colorScheme: {
    light: {
      bayhive: { workflow: bayhiveWorkflowLight },
      // Define both schemes explicitly so the theme engine retains each scale.
      primary: {
        ...createSemanticScale('bayhive.purple'),
        ...createSemanticRgbScale(bayhivePurple),
        ...createSemanticState('bayhive.purple'),
        textColor: '{primary.color}',
        focusRing: { shadow: '0 0 0 0.2rem {primary.200}' },
      },
      success: createSeverityState('bayhive.success'),
      info: {
        ...createSemanticScale('bayhive.info'),
        ...createSemanticState('bayhive.info'),
        textColor: '{info.color}',
        focusRing: { shadow: '0 0 0 0.2rem {info.200}' },
      },
      focusRing: {
        color: '{primary.color}',
        shadow: '0 0 0 0.2rem {primary.200}',
      },
      danger: createSeverityState('bayhive.danger'),
      warn: createSeverityState('bayhive.warn'),
      surface: {
        0: bayhiveBase.white,
        50: '{bayhive.neutral.50}',
        100: '{bayhive.neutral.100}',
        200: '{bayhive.neutral.200}',
        300: '{bayhive.neutral.300}',
        400: '{bayhive.neutral.400}',
        500: '{bayhive.neutral.500}',
        600: '{bayhive.neutral.600}',
        700: '{bayhive.neutral.700}',
        800: '{bayhive.neutral.800}',
        900: '{bayhive.neutral.900}',
        950: '{bayhive.neutral.950}',
      },
      text: {
        color: '{bayhive.neutral.900}',
        hoverColor: '{bayhive.neutral.950}',
        mutedColor: '{bayhive.neutral.600}',
        hoverMutedColor: '{bayhive.neutral.700}',
      },
      content: {
        background: bayhiveBase.white,
        hoverBackground: '{surface.100}',
        borderColor: '{surface.200}',
        color: '{text.color}',
        hoverColor: '{text.hover.color}',
      },
      formField: {
        background: bayhiveBase.white,
        // BayHive surfaces are intentionally lavender. Disabled fields need a
        // separate, unmistakably unavailable state, so use Lara's true-gray
        // neutral ramp instead.
        disabledBackground: '{neutral.200}',
        disabledBorderColor: '{neutral.300}',
        filledBackground: '{surface.50}',
        filledHoverBackground: '{surface.100}',
        filledFocusBackground: '{surface.100}',
        borderColor: '{bayhive.neutral.500}',
        hoverBorderColor: '{primary.color}',
        focusBorderColor: '{primary.color}',
        focusRing: bayhiveFieldFocusRing,
        invalidBorderColor: '{bayhive.danger.500}',
        color: '{bayhive.neutral.900}',
        disabledColor: '{neutral.500}',
        placeholderColor: '{bayhive.neutral.600}',
        invalidPlaceholderColor: '{bayhive.danger.700}',
        floatLabelColor: '{bayhive.neutral.600}',
        floatLabelFocusColor: '{bayhive.purple.600}',
        floatLabelActiveColor: '{bayhive.neutral.600}',
        iconColor: '{bayhive.neutral.500}',
      },
      highlight: {
        background: 'color-mix(in srgb, {bayhive.purple.500}, white 88%)',
        focusBackground: 'color-mix(in srgb, {bayhive.purple.500}, white 80%)',
        color: '{bayhive.purple.800}',
        focusColor: '{bayhive.purple.900}',
      },
      navigation: {
        item: {
          focusBackground: '{surface.100}',
          activeBackground: 'color-mix(in srgb, {bayhive.purple.500}, white 88%)',
          color: '{text.color}',
          focusColor: '{text.hover.color}',
          activeColor: '{bayhive.purple.800}',
          icon: {
            color: '{text.muted.color}',
            focusColor: '{text.hover.color}',
            activeColor: '{bayhive.purple.700}',
          },
        },
      },
      overlay: {
        select: {
          background: bayhiveBase.white,
          borderColor: '{surface.300}',
          color: '{text.color}',
          shadow: '0 8px 24px rgba(24, 33, 47, 0.14), 0 2px 8px rgba(24, 33, 47, 0.1)',
        },
        popover: {
          background: bayhiveBase.white,
          borderColor: '{surface.300}',
          color: '{text.color}',
          shadow: '{overlay.select.shadow}',
        },
        modal: {
          background: bayhiveBase.white,
          borderColor: '{surface.300}',
          color: '{text.color}',
        },
      },
      mask: {
        background: bayhiveMask.light,
        color: '{surface.100}',
      },
    },
    dark: {
      bayhive: { workflow: bayhiveWorkflowDark },
      primary: {
        ...createSemanticScale('bayhive.violet'),
        ...createSemanticRgbScale(bayhiveViolet),
        ...createSemanticState('bayhive.violet', '#000000', true),
        textColor: '{bayhive.violet.200}',
        focusRing: { shadow: darkFocusShadow },
      },
      success: createSeverityState('bayhive.success', true, '#000000'),
      info: {
        ...createSemanticScale('bayhive.violet'),
        ...createSemanticState('bayhive.violet', '#000000', true),
        textColor: '{bayhive.violet.200}',
        focusRing: { shadow: darkFocusShadow },
      },
      focusRing: {
        color: '{bayhive.violet.200}',
        shadow: darkFocusShadow,
      },
      danger: createSeverityState('bayhive.danger', true),
      warn: createSeverityState('bayhive.warn', true, '#000000'),
      surface: bayhiveDark.surface,
      text: bayhiveDark.text,
      content: {
        background: bayhiveDark.content.background,
        hoverBackground: bayhiveDark.content.hoverBackground,
        borderColor: bayhiveDark.content.borderColor,
        color: '{text.color}',
        hoverColor: '{text.hover.color}',
      },
      formField: {
        background: bayhiveDark.formField.background,
        disabledBackground: '{neutral.700}',
        disabledBorderColor: '{neutral.600}',
        filledBackground: bayhiveDark.formField.background,
        filledHoverBackground: bayhiveDark.formField.filledHoverBackground,
        filledFocusBackground: bayhiveDark.formField.filledHoverBackground,
        borderColor: bayhiveDark.formField.borderColor,
        hoverBorderColor: '{primary.color}',
        focusBorderColor: '{primary.color}',
        focusRing: {
          ...bayhiveFieldFocusRing,
          shadow: darkFocusShadow,
        },
        invalidBorderColor: '{bayhive.danger.300}',
        color: '{text.color}',
        disabledColor: '{neutral.400}',
        placeholderColor: bayhiveDark.formField.accentColor,
        invalidPlaceholderColor: '{bayhive.danger.300}',
        floatLabelColor: bayhiveDark.formField.accentColor,
        floatLabelFocusColor: '{primary.text.color}',
        floatLabelActiveColor: bayhiveDark.formField.accentColor,
        iconColor: bayhiveDark.formField.accentColor,
      },
      highlight: {
        background: 'color-mix(in srgb, {primary.400}, transparent 78%)',
        focusBackground: 'color-mix(in srgb, {primary.300}, transparent 70%)',
        color: bayhiveBase.white,
        focusColor: bayhiveBase.white,
      },
      navigation: {
        item: {
          focusBackground: 'color-mix(in srgb, {primary.400}, transparent 74%)',
          activeBackground: 'color-mix(in srgb, {primary.400}, transparent 64%)',
          color: '{text.color}',
          focusColor: '{text.hover.color}',
          activeColor: bayhiveBase.white,
          icon: {
            color: '{text.muted.color}',
            focusColor: '{bayhive.gold.200}',
            activeColor: '{bayhive.gold.200}',
          },
        },
      },
      overlay: {
        select: {
          background: '{surface.800}',
          borderColor: '{surface.600}',
          color: '{text.color}',
          shadow: '0 12px 28px rgba(0, 0, 0, 0.45), 0 3px 10px rgba(0, 0, 0, 0.3)',
        },
        popover: {
          background: '{content.background}',
          borderColor: '{content.border.color}',
          color: '{text.color}',
          shadow: '{overlay.select.shadow}',
        },
        modal: {
          background: '{content.background}',
          borderColor: '{content.border.color}',
          color: '{text.color}',
        },
      },
      mask: {
        background: bayhiveMask.dark,
        color: '{surface.100}',
      },
    },
  },
};

export default bayhiveSemantic;
